/**
 * Remote-control navigation: on TVs there is no mouse or touch, only the
 * arrows, OK and Back. The arrows move the focus to the nearest focusable
 * element in that direction (scrolling it into view), OK clicks it (buttons
 * do that natively), and Back runs the latest registered back handler (close
 * a panel, leave a screen). A clear highlight shows while keys are in use.
 *
 * Screens that handle keys themselves (the player) mark their root with
 * `data-own-keys`; navigation stays out of the way while one is open.
 *
 * A menu marked `data-nav-group` is entered at its current item
 * (`aria-current="true"`), and ▲ ▼ stay inside it.
 */

export type Direction = 'up' | 'down' | 'left' | 'right';

const DIRECTIONS: Record<string, Direction> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };
/** Back: Escape on keyboards, 461 on webOS, 10009 on Tizen, 4 / "GoBack" on Android TV. */
const BACK_KEYS = new Set(['Escape', 'GoBack', 'BrowserBack']);
const BACK_KEY_CODES = new Set([461, 10009]);

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The best target from `from` in `direction`: among boxes lying that way,
 * the closest, with sideways offset counting more than distance (so moving
 * down picks the item below rather than one far down a column aside).
 * Boxes further aside than `maxOffset` don't count: moving right from a list whose
 * neighbour is still loading must not jump to a button far up the screen.
 * Returns the index in `boxes`, or -1.
 */
export function nearestInDirection(from: Box, boxes: Box[], direction: Direction, maxOffset = Infinity): number {
  let best = -1;
  let bestScore = Infinity;
  const fromCenterX = (from.left + from.right) / 2;
  const fromCenterY = (from.top + from.bottom) / 2;
  boxes.forEach((box, index) => {
    let gap: number;
    let offset: number;
    if (direction === 'down' || direction === 'up') {
      gap = direction === 'down' ? box.top - from.bottom : from.top - box.bottom;
      // Overlapping columns count as straight below/above.
      const overlap = Math.min(box.right, from.right) - Math.max(box.left, from.left);
      offset = overlap > 0 ? 0 : Math.abs((box.left + box.right) / 2 - fromCenterX);
    } else {
      gap = direction === 'right' ? box.left - from.right : from.left - box.right;
      const overlap = Math.min(box.bottom, from.bottom) - Math.max(box.top, from.top);
      offset = overlap > 0 ? 0 : Math.abs((box.top + box.bottom) / 2 - fromCenterY);
    }
    // Slightly overlapping boxes still count (rounding, borders).
    if (gap < -4 || offset > maxOffset) return;
    const score = Math.max(0, gap) + offset * 2;
    if (score < bestScore) {
      bestScore = score;
      best = index;
    }
  });
  return best;
}

/* Back handlers: the latest registered one that handles Back wins. */

const backHandlers: (() => boolean)[] = [];

/** Registers a Back handler (returns true when it handled Back); returns the unregister function. */
export function onBack(handler: () => boolean): () => void {
  backHandlers.push(handler);
  return () => {
    const index = backHandlers.lastIndexOf(handler);
    if (index >= 0) backHandlers.splice(index, 1);
  };
}

function isVisible(element: HTMLElement): boolean {
  const box = element.getBoundingClientRect();
  if (box.width === 0 || box.height === 0) return false;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (node.getAttribute('aria-hidden') === 'true' || node.hidden) return false;
  }
  return true;
}

/** Whether the key should move the caret in a text field rather than the focus. */
function keepsKeyInField(element: Element | null, direction: Direction): boolean {
  if (!(element instanceof HTMLInputElement) && !(element instanceof HTMLTextAreaElement)) return false;
  if (direction === 'up' || direction === 'down') return element instanceof HTMLTextAreaElement;
  const { selectionStart, selectionEnd, value } = element;
  // Some input types (number, email) don't report a caret: arrows move the focus.
  if (selectionStart === null || selectionEnd === null) return false;
  if (selectionStart !== selectionEnd) return true;
  return direction === 'left' ? selectionStart > 0 : selectionEnd < value.length;
}

/**
 * Text fields reached with the arrows: on a TV, focusing one opens the
 * on-screen keyboard, which then takes the arrows and traps the viewer. So
 * such a field is "parked" (read-only, no keyboard) while highlighted, and
 * becomes a normal field on OK (the keyboard opens), a typed key, a click or
 * a tap.
 */
const PARKED = 'data-nav-parked';
const NOT_TEXT = new Set(['checkbox', 'radio', 'range', 'color', 'file', 'button', 'submit', 'reset', 'image', 'hidden']);

function isTextField(element: Element | null): element is HTMLInputElement | HTMLTextAreaElement {
  return element instanceof HTMLTextAreaElement || (element instanceof HTMLInputElement && !NOT_TEXT.has(element.type));
}

function park(field: HTMLInputElement | HTMLTextAreaElement): void {
  if (field.readOnly) return;
  field.readOnly = true;
  field.setAttribute(PARKED, '');
}

function unpark(element: EventTarget | null): void {
  if (!isTextField(element as Element) || !(element as Element).hasAttribute(PARKED)) return;
  const field = element as HTMLInputElement | HTMLTextAreaElement;
  field.removeAttribute(PARKED);
  field.readOnly = false;
}

/**
 * Where the focus last was: when the focused element goes away (a panel
 * closes, a list reloads), the next arrow continues from there instead of
 * jumping back to the top.
 */
let lastFocused: { element: HTMLElement; box: Box } | null = null;

/**
 * Smart TV browsers (LG webOS, Samsung Tizen and other TV engines): weak graphics, so the page
 * leaves out costly effects there (`html.tv` in styles.css). Android TV runs the Android shell, not matched.
 */
export function isTvBrowser(agent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean {
  return /Web0S|webOS|Tizen|SMART-TV|SmartTV|NetCast|HbbTV/i.test(agent);
}

/** Moves the focus one step in `direction`; returns whether it moved. */
export function moveFocus(direction: Direction, root: ParentNode = document): boolean {
  // ◀ ▶ inside a row (`data-nav-row`, e.g. a Movies shelf): look only at that row's items, not
  // the whole page; TVs measure every candidate on each press. At the row's end, search the page.
  const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const row = focused && (direction === 'left' || direction === 'right') ? focused.closest<HTMLElement>('[data-nav-row]') : null;
  if (focused && row && (root as Node).contains(row)) {
    const inRow = [...row.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => element !== focused && element.tabIndex >= 0 && isVisible(element));
    const next = inRow[nearestInDirection(focused.getBoundingClientRect(), inRow.map((element) => element.getBoundingClientRect()), direction)];
    if (next) return focusAndShow(next);
  }
  // tabindex="-1" means "not for keys" (mouse-only controls), even on buttons.
  const candidates = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => element.tabIndex >= 0 && isVisible(element));
  if (candidates.length === 0) return false;
  const active = document.activeElement instanceof HTMLElement && candidates.includes(document.activeElement) ? document.activeElement : null;
  let target: HTMLElement | undefined;
  if (!active && lastFocused && !lastFocused.element.isConnected) {
    // The focused element went away: go on from where it was, in the direction pressed (else the nearest).
    const boxes = candidates.map((element) => element.getBoundingClientRect());
    const index = nearestInDirection(lastFocused.box, boxes, direction, sideReach(direction));
    target = candidates[index >= 0 ? index : nearestIndex(lastFocused.box, boxes)];
  } else if (!active) {
    // Nothing focused yet: start where the screen says, else at its first item.
    target = candidates.find((element) => element.hasAttribute('data-autofocus')) ?? candidates.find((element) => element.closest('main')) ?? candidates[0];
  } else {
    const group = active.closest('[data-nav-group]');
    // Inside a menu, ▲ ▼ stay in it (at its end the highlight just stays).
    const pool = group && (direction === 'up' || direction === 'down') ? candidates.filter((element) => group.contains(element)) : candidates;
    const others = pool.filter((element) => element !== active);
    const index = nearestInDirection(active.getBoundingClientRect(), others.map((element) => element.getBoundingClientRect()), direction, sideReach(direction));
    target = index >= 0 ? others[index] : undefined;
    if (!target && group) return true;
    if (!target && (direction === 'left' || direction === 'right')) {
      // The side menu stays reachable from anywhere: on a 1080-high screen its items, at the top,
      // are beyond the reach above for the lower half. Only a menu, never a far-away button.
      const menu = others.filter((element) => element.closest('[data-nav-group]'));
      const nearest = nearestInDirection(active.getBoundingClientRect(), menu.map((element) => element.getBoundingClientRect()), direction);
      target = nearest >= 0 ? menu[nearest] : undefined;
    }
    // Entering a menu from outside lands on its current item.
    const entered = target?.closest('[data-nav-group]');
    if (target && entered && entered !== group) target = entered.querySelector<HTMLElement>('[aria-current="true"]') ?? target;
  }
  if (!target) return false;
  return focusAndShow(target);
}

function focusAndShow(target: HTMLElement): boolean {
  if (isTextField(target)) park(target);
  target.focus({ preventScroll: true });
  lastFocused = { element: target, box: target.getBoundingClientRect() };
  target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  return true;
}

/** How far aside a target may be: a third of the screen across the move (taller for ◀ ▶, wider for ▲ ▼). */
function sideReach(direction: Direction): number {
  if (typeof window === 'undefined') return Infinity;
  return (direction === 'left' || direction === 'right' ? window.innerHeight : window.innerWidth) / 3;
}

function nearestIndex(from: Box, boxes: Box[]): number {
  let best = 0;
  let bestDistance = Infinity;
  boxes.forEach((box, index) => {
    const distance = Math.hypot((box.left + box.right - from.left - from.right) / 2, (box.top + box.bottom - from.top - from.bottom) / 2);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}

/** Whether the viewer is using a remote or arrow keys (not a mouse or touch). */
export function remoteInUse(): boolean {
  return typeof document !== 'undefined' && document.body.classList.contains('keys');
}

/**
 * Keeps a highlight on screen for remote users after a screen change or a closed panel: `target`
 * (e.g. the channel the player was opened from) if it's still there, else the screen's first item.
 * Mouse and touch users (no `keys` class), and an element already focused, are left alone.
 */
export function restoreFocus(target?: Element | null): void {
  if (!remoteInUse()) return;
  // After the new screen has rendered.
  setTimeout(() => {
    const active = document.activeElement;
    // A focused element under a closed screen (hidden) counts as nothing focused.
    if (active instanceof HTMLElement && active !== document.body && active.isConnected && isVisible(active)) return;
    const screen = [...document.querySelectorAll<HTMLElement>('.screen')].find((element) => isVisible(element));
    const start =
      target instanceof HTMLElement && target.isConnected && isVisible(target)
        ? target
        : screen
          ? ([...screen.querySelectorAll<HTMLElement>(FOCUSABLE)].find((element) => element.hasAttribute('data-autofocus') && isVisible(element)) ??
            [...screen.querySelectorAll<HTMLElement>(FOCUSABLE)].find((element) => element.tabIndex >= 0 && isVisible(element)))
          : undefined;
    if (!start) return;
    // A text field waits for OK before the TV's on-screen keyboard opens (as with the arrows).
    if (isTextField(start)) park(start);
    start.focus({ preventScroll: true });
    lastFocused = { element: start, box: start.getBoundingClientRect() };
    start.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, 50);
}

/** Starts listening for remote-control keys; returns the stop function. */
export function startSpatialNavigation(): () => void {
  const body = document.body;
  if (isTvBrowser()) document.documentElement.classList.add('tv');
  const onKey = (event: KeyboardEvent) => {
    if (event.defaultPrevented || document.querySelector('[data-own-keys]')) return;
    const active = document.activeElement;
    const typing = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
    if (BACK_KEYS.has(event.key) || BACK_KEY_CODES.has(event.keyCode) || (event.key === 'Backspace' && !typing)) {
      for (let i = backHandlers.length - 1; i >= 0; i--) {
        if (backHandlers[i]!()) {
          event.preventDefault();
          break;
        }
      }
      return;
    }
    const direction = DIRECTIONS[event.key];
    const parked = isTextField(active) && active.hasAttribute(PARKED);
    if (parked && !direction) {
      unpark(active);
      if (event.key === 'Enter') {
        // OK on a highlighted field: start typing (on a TV, this opens the on-screen keyboard).
        event.preventDefault();
        active.blur();
        active.focus({ preventScroll: true });
      }
      return;
    }
    if (!direction) return;
    body.classList.add('keys');
    if (!parked && keepsKeyInField(active, direction)) return;
    if (moveFocus(direction)) event.preventDefault();
  };
  // Mouse or touch: the highlight goes away until a key is used again, and a clicked field is a normal one.
  const onPointer = (event: Event) => {
    body.classList.remove('keys');
    unpark(event.target);
  };
  const onFocus = (event: FocusEvent) => {
    if (event.target instanceof HTMLElement && event.target !== body) lastFocused = { element: event.target, box: event.target.getBoundingClientRect() };
  };
  // Before a focused element is removed, remember where it was (its box is gone afterwards).
  const onFocusOut = (event: FocusEvent) => {
    unpark(event.target);
    if (event.target instanceof HTMLElement) lastFocused = { element: event.target, box: event.target.getBoundingClientRect() };
  };
  document.addEventListener('focusin', onFocus);
  document.addEventListener('focusout', onFocusOut);
  window.addEventListener('keydown', onKey);
  window.addEventListener('mousedown', onPointer);
  window.addEventListener('touchstart', onPointer, { passive: true });
  return () => {
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('mousedown', onPointer);
    window.removeEventListener('touchstart', onPointer);
    document.removeEventListener('focusin', onFocus);
    document.removeEventListener('focusout', onFocusOut);
  };
}
